import {
  Component, ElementRef, ChangeDetectionStrategy, Input, Output,
  EventEmitter, ViewChild, Inject, ChangeDetectorRef, OnInit, OnDestroy
} from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';

import { RANDOM_ID } from '@app/constants/general.constants';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { Airport } from '@app/modules/core/interfaces/airport';
import { AutocompletePlaces } from '@search-bar/entities/autocomplete-places';
import { SearchType } from '@search-bar/entities/search-type';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { TOGGLE_SEARCH_PAGE_DELAY } from '@shared/base-search-bar/constants/search.constants';
import { TooltipOrientation } from '@app/modules/shared/tooltip/entities/tooltip-orientation';
import { EventProperties } from '@avo/interface/trigger-props';
import { locationNotFoundAmplitudePropsMapper } from '@app/modules/core/mappers/amplitude-mappers';
import { BaseSearchInputAbstractComponent } from '@shared/base-search-bar/abstract/base-search-input.abstract';
import { InputTextSize } from '@design-system/components/molecules/input-text/entities/input-text.types';

@Component({
  selector: 'app-search-place-input',
  templateUrl: './search-place-input.component.html',
  styleUrls: ['./search-place-input.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SearchPlaceInputComponent extends BaseSearchInputAbstractComponent implements OnInit, OnDestroy {

  @Input() searchType: SearchType;
  @Input() place: AutocompletePlace;
  @Input() placeType: PlaceType;
  @Input() countryCode: string;
  @Input() countryName: string;
  @Input() set autoCompletePlaces(autoCompletePlaces: AutocompletePlaces) {
    this._autoCompletePlaces = autoCompletePlaces;
    setTimeout(() => this.sendNotFoundEvent(), TOGGLE_SEARCH_PAGE_DELAY);
  }
  @Input() airports: Airport[] = [];
  @Input() delay: number = 500;
  @Input() eventPath: EventTarget[];
  @Input() isMobile: boolean = false;
  @Input() autofocus: boolean = false;
  @Input() selected: boolean;
  @Input() isLoadingAutocompletePlaces: boolean = false;
  @Input() samePlaceError = false;
  @Input() activateCursor: boolean = false;
  @Input() isPackageInputDesktop: boolean;
  @Input() arrivalPlace: AutocompletePlace;
  @Input() departurePlace: AutocompletePlace;
  @Input() textTooltipTop: number = 10;
  @Input() resultsPage = false;

  @Output() selectPlace: EventEmitter<SelectAutocompletePlace> = new EventEmitter<SelectAutocompletePlace>();
  @Output() locationNotFoundEvent: EventEmitter<EventProperties> = new EventEmitter<EventProperties>();
  @Output() popularSelected: EventEmitter<EventProperties> = new EventEmitter<EventProperties>();

  @ViewChild('destination', { static: true }) public destination: ElementRef;

  public id: number = Math.floor(Math.random() * RANDOM_ID);
  public tooltipOrientation: string = TooltipOrientation.LEFT;
  private _autoCompletePlaces: AutocompletePlaces;

  constructor(
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) { 
    super(changeDetectorRef);
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      if (this.place && this.place.code) this.countryName = this.defaultCountryFormat;
    }
  }

  ngOnDestroy(): void {
    this.removeAutoCompletePlaces.emit(); 
  }

  get inputSize(): InputTextSize {
    return !this.isMobile && (this.isResultsPage || this.isHotelDetailPackageDesktop) ? InputTextSize.MD : InputTextSize.RG;
  }

  get filled(): boolean {
    return !this.isMobile && !this.isResultsPage && !this.isHotelDetailPackageDesktop;
  }

  get staticFilled(): boolean {
    return !this.isMobile && (this.isResultsPage || this.isHotelDetailPackageDesktop);
  }

  get sendLocationNotFoundEvent(): boolean {
    return this.showInput && !!this.value && this.resultsPlaces?.cities?.length === 0 &&
      this.resultsPlaces?.airports?.length === 0;
  }

  get sameDestinationMessage(): string {
    return this.placeType === PlaceType.ARRIVAL ? 'SEARCH_MODULE.PLACE_TOOLTIP.ARRIVAL' : 'SEARCH_MODULE.PLACE_TOOLTIP.DEPARTURE';
  }

  get emptyInputErrorMessage(): string {
    return this.placeType === PlaceType.DEPARTURE ? 
      'SEARCH_MODULE.LOCATION_SEARCH.SELECT_ORIGIN_ERROR' : 
      'SEARCH_MODULE.LOCATION_SEARCH.SELECT_DESTINATION_ERROR';
  }

  get isArrival(): boolean {
    return this.placeType === PlaceType.ARRIVAL; 
  }
  get autoCompletePlaces(): AutocompletePlaces {
    return this._autoCompletePlaces; 
  }
  get inputPlaceId(): string {
    return `active-search-place-input-${this.placeType}`; 
  }

  get showCursorHome(): boolean {
    const validCondition = this.searchType === SearchType.HOME_FLIGHT
      || this.searchType === SearchType.HOME_HOTELS || this.searchType === SearchType.HOME_BUSES;
    return validCondition && this.activateCursor && !this.hasPlaceSelected;
  }

  get isResultPageBus(): boolean {
    return this.isResultsPage && this.searchBuses; 
  }

  get hasPlaceSelected(): boolean {
    return !!this.place.code; 
  }

  get defaultCityCode(): string {
    return this.place ? this.searchBuses ? this.defaultBusesCityCode : this.place.code : null;
  }

  get defaultBusesCityCode(): string {
    return this.place.description ? `${this.place.description}${this.getStateCode(this.place.statecode)}` : null;
  }

  get defaultCountry(): string {
    return (!isEmpty(this.place) ?
      (this.place ? this.searchBuses ? this.defaultCountryFormat 
        : this.defaultCountryFormat : this.place.description) : null);
  }

  get defaultCountryMobile(): string {
    return (!isEmpty(this.place) ?
      (this.place.country ? this.searchBuses ? this.place.country : 
        this.defaultCountryFormatMobile : this.place.description) : null);
  }

  get defaultCityName(): string {
    return (this.place ? this.searchBuses ? this.place.code : this.place.code : null);
  }

  get placeTypeText(): string {
    return this.placeType === PlaceType.DEPARTURE ? 'ORIGIN' : 'DESTINATION';
  }

  get defaultCountryFormat(): string {
    const defaultCountryString = this.place && this.place.code ? `${this.place.description} (${this.place.code})` : '';
    return defaultCountryString.split(',').filter((item, i, allItems) => i === allItems.indexOf(item)).join(',');
  }

  get defaultCountryFormatMobile(): string {
    const defaultCountryString = this.place && this.place.code ? `${this.place.description}, ${this.place.country}` : '';
    return defaultCountryString.split(',').filter((item, i, allItems) => i === allItems.indexOf(item)).join(',');
  }

  get defaultCountryName(): string {
    const defaultCountryName = this.place && this.place.code ? `${this.place.description} , ${this.place.country}` : '';
    return defaultCountryName;
  }

  get resultsPlaces(): AutocompletePlaces {
    return { airports: this.autoCompletePlaces?.airports, cities: this.autoCompletePlaces?.cities };
  }

  get isResultsPage(): boolean {
    return !this.isMobile ? this.searchType === SearchType.FLIGHT_RESULTS || this.searchType === SearchType.BUSES_RESULTS ||
      this.searchType === SearchType.PACKAGES_RESULT || this.isHotelDetailPackageDesktop || this.resultsPage : false;
  }

  get isHotelDetailPackageDesktop(): boolean {
    return !this.isMobile && this.isPackageInputDesktop && this.searchType === SearchType.HOTELS_DETAIL;
  }

  get searchBuses(): boolean {
    return this.searchType === SearchType.HOME_BUSES || this.searchType === SearchType.BUSES_RESULTS;
  }

  get searchTourAndActivities(): boolean {
    return this.searchType === SearchType.TOUR_AND_ACTIVITIES;
  }

  get mergeAirportsAndCities(): boolean {
    return !this.searchTourAndActivities && !this.searchBuses;
  }

  public emitSelectPlace(place: AutocompletePlace, isAirport: boolean = false): void {
    this.toggleInput();
    this.selectPlace.emit({ ...place, is_airport: isAirport, type: this.placeType });
  }

  public selectAirportOrCity(data: { place: AutocompletePlace, isAirport: boolean }): void {
    this.emitSelectPlace(data?.place, data?.isAirport)
  }

  private getStateCode(statecode: string): string {
    if (this.searchBuses && statecode) {
      const splitStateCode = statecode.split('_')[1];
      return splitStateCode ? ` (${splitStateCode})` : ` (${statecode})`;
    } else return '';
  }

  private sendNotFoundEvent(): void {
    if (this.sendLocationNotFoundEvent) {
      this.locationNotFoundEvent.emit(
        locationNotFoundAmplitudePropsMapper(this.searchType, this.value, this.inputPlaceId));
    }
  }
}
